import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ContentTrackerProps } from './types';
import { FullScreenTrackingContext } from './FullScreenTrackingContext';
import { getTextContentURL } from '../../Content/helper';
import { WindowFocusContext } from './WindowFocusContext';
import { TextMetadata } from '../../../definitions/schemas';
import { TrackEvent } from '../../../enums/Content/TrackEvent';
import RoomTextContent, {
  RoomTextContentProps,
} from '../Content/RoomTextContent';
import SimpleTracker from '../../../Services/Analytics/SimpleTracker';
import TrackInterface from '../../../graphql/Rooms/TrackInterface';
import useIntersectionObserver from '../../../lib/hook/useIntersectionObserver';

interface Props extends RoomTextContentProps, ContentTrackerProps {
  /**
   * Optional children to use the tracker as a Higher Order Component that
   * grants the tracking capabilities, instead of relying on its default UI
   */
  children?: React.ReactNode;
  onView?: () => void;
}

const TextTracker: React.FC<Props> = (props: Props): JSX.Element => {
  const { fullScreenContentId } = useContext(FullScreenTrackingContext);
  const { isWindowFocused } = useContext(WindowFocusContext);

  const {
    children,
    contactId,
    contentId,
    html,
    onView,
    roomId,
    text,
    track,
    version,
    ...roomTextContentProps
  } = props;

  const [withinViewport, setWithinViewport] = useState(false);

  const isUrl = useMemo(() => {
    const [, isTextContentUrl] = getTextContentURL(text);
    return isTextContentUrl;
  }, [text]);

  const { current: tracker } = useRef(new SimpleTracker());

  const viewRef = useRef(false);

  const ref = useIntersectionObserver<HTMLDivElement>(
    (isIntersecting: boolean) => setWithinViewport(isIntersecting)
  );

  useEffect(() => {
    if (track) {
      tracker.attach(trackEvent);
    }

    return () => tracker.detach();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    /**
     * Activating tracking when these rules are met:
     *  1. The content is within viewport
     *  2. The window/tab must be focused in order to prevent tracking while the tab
     *     is loading in the background or the user jumps to another window
     *  3. There is no content displaying currently content in full screen
     *  4. OR the current content that's in full screen is this content
     */
    const isAnyContentInFullScreen = !!fullScreenContentId;
    const isCurrentlyInFullScreen = fullScreenContentId === contentId;

    const isTrackerActive =
      !!track &&
      withinViewport &&
      isWindowFocused &&
      (!isAnyContentInFullScreen || isCurrentlyInFullScreen);

    tracker.setActive(isTrackerActive);

    // plain text contents are "viewed" as soon as they are within the viewport,
    // but avoid "view" them multiple times if the content exists and re-enters
    // the viewport
    if (isTrackerActive && !isUrl && !viewRef.current && onView) {
      onView();
      viewRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUrl, isWindowFocused, withinViewport]);

  /**
   * Stores the click metadata, so it's sent as part of the tracking
   * information for next text tracking events.
   */
  const onContentClicked = () => {
    if (!isUrl) {
      return;
    }

    // tracking the click immediately, but we send the measure as 0 to
    // not add extra time spent on links, this is better than storing the
    // metadata and wait for the next tick
    trackEvent(0, {
      clicked: true,
    });

    // for link urls, they are viewed when the user clicks on the link
    if (onView) {
      onView();
    }
  };

  const trackEvent = (elapsedTime: number, metadata?: TextMetadata) => {
    if (!(track && roomId && version)) {
      return;
    }

    try {
      new TrackInterface(contactId).track({
        contentId,
        event: TrackEvent.TEXT_READ_SECONDS,
        measure: elapsedTime,
        metadata,
        roomId,
        version,
      });
    } catch (e) {
      // user shouldn't be alerted about this fail, but we're catching
      // this exception to avoid "Uncaught promise reject" warnings
    }
  };

  if (children) {
    return React.cloneElement(children as any, {
      onClick: onContentClicked,
    });
  }

  return (
    <RoomTextContent
      html={html}
      ref={ref}
      onClick={onContentClicked}
      text={text}
      {...roomTextContentProps}
    />
  );
};

export default TextTracker;
